import i18n from 'src/i18n';
import { hasValue } from 'src/shared/utils/common';
import { NotificationsStore } from 'src/store/notifications-store/notifications-store';

import { showCustomErrorMessage } from '../../components/show-custom-error-message';
import { FormStore } from '../form.entity';
import { GeologicalLoadService } from '../geological-load/geological-load-service';

import { RigOperationSection } from './rig-operation-section.entity';
import { RigOperationStage } from './rig-operation-stage.entity';
import { RigOperation } from './rig-operation.entity';
import { RigOperationsService } from './rig-operations-service';

export class RigOperationsValidation {
  private readonly rigOperationsService: RigOperationsService;
  private readonly geoLoadService: GeologicalLoadService;
  private readonly notificationsService: NotificationsStore;
  private readonly formStore: FormStore;

  constructor(
    notificationsService: NotificationsStore,
    formStore: FormStore,
    rigOperationsService: RigOperationsService,
    geoLoadService: GeologicalLoadService,
  ) {
    this.rigOperationsService = rigOperationsService;
    this.geoLoadService = geoLoadService;
    this.notificationsService = notificationsService;
    this.formStore = formStore;
  }

  validateFormBeforeRestoreRigOperationsToDirectoryValues(
    rigOperation: RigOperation,
    shouldCalculateCSandPVPerRigOperation: boolean,
  ): boolean {
    let isValid = true;

    const errors: string[] = [];
    const firstStage = rigOperation.stages[0];

    // TODO: localization
    if (!firstStage) {
      errors.push(i18n.t('Необходимо создать один этап и указать у него дату начала'));
      isValid = false;
    }

    if (firstStage && !firstStage.startDate) {
      errors.push(i18n.t('Укажите дату начала у первого этапа подхода, который хотите привести к справочным данным'));
      isValid = false;
    }

    if (!rigOperation.rigId) {
      errors.push(i18n.t('Выберите Буровую установку'));
      isValid = false;
    }

    if (shouldCalculateCSandPVPerRigOperation) {
      const { block, field, fluidType, wellType, wellPurpose } = this.formStore.fields;

      if (!block?.value || !field?.value) {
        errors.push(i18n.t('Выберите куст'));
        isValid = false;
      }

      if (!wellPurpose?.value) {
        errors.push(i18n.t('Выберите вид скважины'));
        isValid = false;
      }

      if (!fluidType?.value) {
        errors.push(i18n.t('Выберите назначение скважины'));
        isValid = false;
      }

      if (!wellType?.value) {
        errors.push(i18n.t('Выберите конструкцию скважины'));
        isValid = false;
      }
    } else {
      const { commercialSpeed, passingValue } = this.formStore.fields;

      if (!commercialSpeed?.value) {
        errors.push(i18n.t('Внесите значение коммерческой скорости на вкладке с основными данными'));
        isValid = false;
      }

      if (!passingValue?.value) {
        errors.push(i18n.t('Внесите значение проходки на вкладке с основными данными'));
        isValid = false;
      }
    }

    if (errors.length) {
      showCustomErrorMessage(
        'Чтобы привести подход к справочным значениям сначала выполните следующие шаги:',
        errors.reverse(),
        0,
      );
    }

    return isValid;
  }

  validateStartDate(rigOperation: RigOperation, stage: RigOperationStage, section?: RigOperationSection): boolean {
    let isValid = true;

    const source = section ?? stage;

    if (!source.startDate) {
      isValid = false;

      source.addError('startDate', 'Поле обязательно для заполнения');
    }

    if (hasValue(source.startDate) && hasValue(source.endDate) && source.startDate > source.endDate) {
      isValid = false;

      source.addError('startDate', 'Дата начала не может быть больше даты окончания');
    }

    return isValid;
  }

  validateEndDate(rigOperation: RigOperation, stage: RigOperationStage, section?: RigOperationSection): boolean {
    let isValid = true;

    const source = section ?? stage;

    if (!source.endDate) {
      isValid = false;

      source.addError('endDate', 'Поле обязательно для заполнения');
    }

    if (hasValue(source.startDate) && hasValue(source.endDate) && source.startDate > source.endDate) {
      isValid = false;

      source.addError('endDate', 'Дата окончания не может быть меньше даты начала');
    }

    return isValid;
  }

  validatePassingValueMinField(
    rigOperation: RigOperation,
    stage: RigOperationStage,
    section: RigOperationSection,
  ): boolean {
    let isValid = true;

    if (
      hasValue(section.passingValueMin) &&
      hasValue(section.passingValueMax) &&
      section.passingValueMin > section.passingValueMax
    ) {
      isValid = false;

      section.addError('passingValueMin', 'Интервкал "От" не может быть больше интервала "До"');
    }

    return isValid;
  }
  validatePassingValueMaxField(
    rigOperation: RigOperation,
    stage: RigOperationStage,
    section: RigOperationSection,
  ): boolean {
    let isValid = true;

    if (
      hasValue(section.passingValueMin) &&
      hasValue(section.passingValueMax) &&
      section.passingValueMin > section.passingValueMax
    ) {
      isValid = false;

      section.addError('passingValueMin', 'Интервкал "До" не может быть меньше интервала "От"');
    }

    return isValid;
  }

  validatePassingValueField(
    rigOperation: RigOperation,
    stage: RigOperationStage,
    section?: RigOperationSection,
  ): boolean {
    let isValid = true;

    const source = section ?? stage;

    if (
      source instanceof RigOperationStage &&
      (source.sections.length > 0 ||
        !this.rigOperationsService.isDrillingStage(source.wellLifeCycleStageId) ||
        this.rigOperationsService.rigOperations.length < 2)
    ) {
      return true;
    }

    if (!hasValue(source.passingValue)) {
      isValid = false;

      source.addError('passingValue', 'Поле обязательно для заполнения');
    } else if (source.passingValue <= 0) {
      isValid = false;

      source.addError('passingValue', 'Проходка не может быть меньше или равной нулю');
    }

    return isValid;
  }

  validateDurationField(rigOperation: RigOperation, stage: RigOperationStage, section?: RigOperationSection): boolean {
    let isValid = true;
    const source = section ?? stage;

    if (source instanceof RigOperationStage && source.sections.length > 0) {
      return true;
    }

    if (!hasValue(source.duration)) {
      isValid = false;

      source.addError('duration', 'Поле обязательно для заполнения');
    } else if (source.duration <= 0) {
      isValid = false;

      source.addError('duration', 'Продолжительность не может быть меньше или равной нулю');
    } else if (this.rigOperationsService.isDrillingStage(stage.wellLifeCycleStageId)) {
      const sourceGeoLoadDuration = ((): number => {
        const rigOperationGeoLoad = this.geoLoadService.geoLoadRigOperationsByRigOperationIdMap.get(
          rigOperation.entityId,
        );
        const stageGeoLoad = rigOperationGeoLoad?.stages.find((st) => st.stageEntityId === stage.entityId);
        const sectionGeoLoad = section
          ? stageGeoLoad?.sections.find((sec) => sec.sectionEntityId === section.entityId)
          : null;

        if (!stageGeoLoad || (section && !sectionGeoLoad)) {
          isValid = false;

          if (section && !sectionGeoLoad) {
            section.addError('duration', 'Невозможно связать секцию с геологической нагрузкой');
          }

          if (!stageGeoLoad) {
            stage.addError('duration', 'Невозможно связать этап с геологической нагрузкой');
          }

          return 0;
        }

        const source = (sectionGeoLoad ?? stageGeoLoad).geologicalLoad;

        return (
          (source.coreSamplingDayAmount ?? 0) +
          (source.daysAmountOfDataInterpretationOperation ?? 0) +
          (source.testingFormationWorkDayAmount ?? 0)
        );
      })();

      if (sourceGeoLoadDuration > source.duration) {
        isValid = false;

        source.addError('duration', 'Продолжительность не может быть меньше суммы дней геологической нагрузки');
      }
    }

    return isValid;
  }

  validateSectionType(rigOperation: RigOperation, stage: RigOperationStage, section: RigOperationSection) {
    let isValid = true;

    if (!section.sectionTypeId) {
      isValid = false;

      section.addError('sectionTypeId', 'Поле обязательно для заполнения');
    }

    return isValid;
  }

  validateStageType(rigOperation: RigOperation, stage: RigOperationStage): boolean {
    let isValid = true;

    if (!stage.wellLifeCycleStageId) {
      isValid = false;

      stage.addError('wellLifeCycleStageId', 'Поле обязательно для заполнения');
    }

    return isValid;
  }

  validateCommercialSpeed(rigOperation: RigOperation, stage: RigOperationStage): boolean {
    let isValid = true;

    if (
      this.rigOperationsService.isDrillingStage(stage.wellLifeCycleStageId) &&
      this.rigOperationsService.rigOperations.length > 1
    ) {
      if (!hasValue(stage.commercialSpeed)) {
        isValid = false;

        stage.addError('commercialSpeed', 'Поле обязательно для заполнения');
      } else if (stage.commercialSpeed <= 0) {
        isValid = false;

        stage.addError('commercialSpeed', 'КС не может быть меньше или равной нулю');
      }
    }

    return isValid;
  }

  validateRigId(rigOperation: RigOperation): boolean {
    let isValid = true;

    if (!rigOperation.rigId) {
      isValid = false;

      rigOperation.addError('rigId', 'Поле обязательно для заполнения');
    }

    return isValid;
  }

  validateSection(rigOperation: RigOperation, stage: RigOperationStage, section: RigOperationSection): boolean {
    let isValid = true;

    const isValidSectionType = this.validateSectionType(rigOperation, stage, section);
    const isValidDuration = this.validateDurationField(rigOperation, stage, section);
    const isValidStartDate = this.validateStartDate(rigOperation, stage, section);
    const isValidEndDate = this.validateEndDate(rigOperation, stage, section);
    const isValidPassingValue = this.validatePassingValueField(rigOperation, stage, section);
    const isValidPassingValueMin = this.validatePassingValueMinField(rigOperation, stage, section);
    const isValidPassingValueMax = this.validatePassingValueMaxField(rigOperation, stage, section);

    if (
      !isValidPassingValue ||
      !isValidDuration ||
      !isValidPassingValueMin ||
      !isValidPassingValueMax ||
      !isValidSectionType ||
      !isValidStartDate ||
      !isValidEndDate
    ) {
      isValid = false;
    }

    return isValid;
  }

  validateStage(rigOperation: RigOperation, stage: RigOperationStage): boolean {
    let isValid = true;

    const isValidStartDate = this.validateStartDate(rigOperation, stage);
    const isValidEndDate = this.validateEndDate(rigOperation, stage);
    const isValidStageType = this.validateStageType(rigOperation, stage);

    if (!isValidStartDate || !isValidEndDate || !isValidStageType) {
      isValid = false;
    }

    if (!stage.sections.length) {
      const isValidPassingValue = this.validatePassingValueField(rigOperation, stage);
      const isValidDuration = this.validateDurationField(rigOperation, stage);
      const isValidCommercialSpeed = this.validateCommercialSpeed(rigOperation, stage);

      if (!isValidPassingValue || !isValidDuration || !isValidCommercialSpeed) {
        isValid = false;
      }
    } else {
      const isValidSections = stage.sections.reduce((isValid, sec) => {
        if (!this.validateSection(rigOperation, stage, sec)) {
          isValid = false;
        }

        return isValid;
      }, true);

      if (!isValidSections) {
        isValid = false;
      }
    }

    return isValid;
  }

  validateRigOperation(rigOperation: RigOperation): boolean {
    let isValid = true;

    const isValidRigId = this.validateRigId(rigOperation);
    const isValidStages = rigOperation.stages.reduce((isValid, s) => {
      if (!this.validateStage(rigOperation, s)) {
        isValid = false;
      }

      return isValid;
    }, true);

    if (!isValidStages || !isValidRigId) {
      isValid = false;
    }

    return isValid;
  }

  validateRigOperations(): boolean {
    return this.rigOperationsService.rigOperations.reduce((isValid, rigOperation) => {
      if (!this.validateRigOperation(rigOperation)) {
        isValid = false;
      }

      return isValid;
    }, true);
  }
}
